import { DateTime } from 'luxon';
import { derived, get, writable } from 'svelte/store';
import type { Contract, PaymentMethod, Price, Tax } from '../model/billing';
import { apiStore } from './api';
import { FormStatus, formStatus } from './form';

export const contractStore = writable<Contract>();
export const paymentMethodStore = writable<PaymentMethod>();
export const priceStore = writable<Price[]>([]);

export const isContractValid = derived(contractStore, ($contractStore) => {
	if (!$contractStore) return true;
	return $contractStore.sanctionLevel <= 1 && $contractStore.billingEnabled;
});

export function convertContractApiToContract<T extends Contract>(contract: T): T {
	return {
		...contract,
		nextInvoiceDate: contract.nextInvoiceDate
			? DateTime.fromISO(contract.nextInvoiceDate as unknown as string)
			: null,
		sanctionLevelDegradeDate: contract.sanctionLevelDegradeDate
			? DateTime.fromISO(contract.sanctionLevelDegradeDate as unknown as string)
			: null
	};
}

export function billingNextInvoiceSubscriptionAmount(contract: Contract): number {
	return (
		contract.subscriptionAmount -
		((contract.nextInvoiceDiscountAmount > 0
			? contract.nextInvoiceDiscountAmount
			: contract.permanentDiscountAmount) +
			(contract.subscriptionAmount *
				(contract.nextInvoiceDiscountPercent > 0
					? contract.nextInvoiceDiscountPercent
					: contract.permanentDiscountPercent)) /
				100)
	);
}

export function billingNextInvoiceAmount(contract: Contract): number {
	return (
		billingNextInvoiceSubscriptionAmount(contract) +
		contract.nextInvoiceAdditionalLines.reduce((acc, line) => acc + line.amount, 0)
	);
}

export function cancelSubscription() {
	get(apiStore)
		?.delete('/contract/subscribe')
		.then((res) => {
			contractStore.set(convertContractApiToContract(res.data));
		});
}

export function reactivateSubscription() {
	formStatus.set(FormStatus.Processing);
	get(apiStore)
		?.post('/contract/subscribe')
		.then((res) => {
			formStatus.set(FormStatus.Success);
			contractStore.set(convertContractApiToContract(res.data));
		});
}

export function loadContract(forceReload = false) {
	if (forceReload || !get(contractStore)) {
		get(apiStore)
			?.get<Contract>('/contract')
			.then((response) => loadContractFromData(response.data));
	}
}

export function loadContractFromData(data: Contract) {
	contractStore.set({
		...data,
		// Convert the date to DateTime
		nextInvoiceDate: DateTime.fromISO(data.nextInvoiceDate as unknown as string),
		sanctionLevelDegradeDate: DateTime.fromISO(
			data.sanctionLevelDegradeDate as unknown as string
		)
	});
}

export function loadPrices(forceReload = false) {
	if (forceReload || get(priceStore).length === 0) {
		get(apiStore)
			?.get<Price[]>('/contract/prices')
			.then((response) => {
				priceStore.set(
					response.data.sort(
						(a: Price, b: Price) => a.prices[0].amount - b.prices[0].amount
					)
				);
			});
	}
}

export const billingTaxes = writable<Tax[]>([]);
